import cn from 'classnames';
import { isEqual } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { targetingConstants } from 'sonata-platform-components';
import { Checkbox, Tabs, TabsItemProps, Toast, type ValidationErrors } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { FormExpandableSection } from '@/components/common/FormExpandableSection';
import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { DealsSelection } from './DealsSelection/DealsSelection';
import { getAppAndSiteMarketplaceTags } from './helpers/getSectionHeaderTags.helper';
import { getTabItemsCount } from './helpers/getTabItemsCount.helper';
import { omitEmptyStrictSearches } from './Inventory/helpers/strictSearches.helper';
import { Inventory } from './Inventory/Inventory';

import './appAndSiteMarketplace.scss';

import type { LineItemGoal } from 'sonata-static-storage/lib/entities';
import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';

type TabType = 'generic' | 'specific';

type Props = {
  error?: ValidationErrors;
  entityId: string;
  /** Entity goals, used with `pricingModelConfig` to filter deals for performance pricing models. */
  goals?: LineItemGoal[];
  id: string;
  /** Entity pricing config, used to hide deals whose bid floor exceeds the max premium CPM after fees. */
  pricingModelConfig?: PricingModelConfig;
  sessionId: string;
};

export function AppAndSiteMarketplace({ error, entityId, goals, id, pricingModelConfig, sessionId }: Props) {
  const { formatMessage } = useIntl();
  const [tab, setTab] = useState<TabType>('generic');
  const [showErrorToast, setShowErrorToast] = useState(true);

  const {
    familyType,
    resetSection,
    setUnknownIncluded,
    showDeals,
    targeting: { appSiteConfiguration, deals, unknownIncluded },
    user,
    userRoles
  } = useTargetingContext();
  const { DEFAULT_APP_AND_SITE_CONFIGURATION } = targetingConstants;

  const handleTabsChange = (tabSelected: TabType) => {
    setTab(tabSelected);
  };

  const handleResetAppAndSiteMarketplace = () => {
    resetSection('appAndSiteMarketplace');
    setShowErrorToast(false);
  };

  const tabTitles: Record<TabType, string> = {
    generic: formatMessage({
      defaultMessage: 'Generic Sites (entire domains)',
      id: 'appAndSiteMarketplace.tabs.genericSite'
    }),
    specific: formatMessage({
      defaultMessage: 'App Bundles & Site IDs',
      id: 'appAndSiteMarketplace.tabs.specificSite'
    })
  };

  const tabs: TabsItemProps[] = (['generic', 'specific'] as TabType[]).map((type: TabType) => {
    const itemsCount = getTabItemsCount(appSiteConfiguration, type);
    return {
      badge: itemsCount > 0 ? itemsCount : undefined,
      className: cn('appAndSiteMarketplace__tabsItem', {
        'appAndSiteMarketplace__tabsItem--selected': tab === type
      }),
      component: (
        <Inventory
          entityId={entityId}
          id={`inventory${entityId}${type}`}
          key={`${type}-inventory`}
          sessionId={sessionId}
          type={type}
        />
      ),
      id: type,
      title: tabTitles[type]
    };
  });

  const isSectionOpened =
    !isEqual(omitEmptyStrictSearches(appSiteConfiguration), DEFAULT_APP_AND_SITE_CONFIGURATION) || !!deals.length;
  const hasAppSiteFiltering = (['generic', 'specific'] as TabType[]).some(
    type => getTabItemsCount(appSiteConfiguration, type) > 0
  );
  const showUnknownDomain =
    checkUserResourcePermissions('unknownIncluded', user, userRoles) &&
    familyType !== CampaignFamilyEnum.dooh &&
    (!!deals.length || hasAppSiteFiltering);
  const appAndSiteMarketplaceTags = getAppAndSiteMarketplaceTags(appSiteConfiguration, deals);

  return (
    <div className="appAndSiteMarketplace" id={id}>
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        hasErrors={Boolean(error?.deals?.message)}
        onReset={handleResetAppAndSiteMarketplace}
        preffixId={id}
        tags={appAndSiteMarketplaceTags}
        title={formatMessage({
          defaultMessage: 'App & Site Marketplace',
          id: 'appAndSiteMarketplace.title'
        })}
        tooltipText={formatMessage({
          defaultMessage:
            'Apply allowlists or blocklists of specific apps and websites to strictly control your inventory sources.',
          id: 'appAndSiteMarketplace.tooltip'
        })}
      >
        {error?.deals?.message && showErrorToast && (
          <Toast
            autoClose={false}
            className="settings__errorToast"
            closeToast={() => setShowErrorToast(false)}
            icon="fal fa-exclamation-triangle"
            text={error.deals.message}
            type="danger"
          />
        )}
        {showDeals && <DealsSelection goals={goals} pricingModelConfig={pricingModelConfig} />}
        {familyType !== CampaignFamilyEnum.dooh && (
          <Tabs
            className="appAndSiteMarketplace__tabs"
            hideNoneActivePanels
            onChangeTab={(tabId: string) => handleTabsChange(tabId as TabType)}
            tabSelected={tab}
            tabs={tabs}
            variant="filled"
          />
        )}
        {showUnknownDomain && (
          <Checkbox
            active={unknownIncluded.domain ?? true}
            classes="appAndSiteMarketplace__unknownDomain"
            id="appAndSiteMarketplaceUnknownDomain"
            labelText={formatMessage({
              defaultMessage: 'Target Unknown Domain',
              id: 'appAndSiteMarketplace.unknownIncluded'
            })}
            onClickAction={(_: string, checked: boolean) => setUnknownIncluded('domain', checked)}
            type="checkbox"
          />
        )}
      </FormExpandableSection>
    </div>
  );
}
